/**
 * Édition du contenu d'un brouillon de gamme à l'écran : paliers, seuils, lignes de pièces.
 *
 * Fonctions pures, sans React : c'est ici que se décide ce qui part vers l'API, ce qu'on refuse
 * avant l'envoi, et comment on traduit un refus de l'API. Donc c'est ici que cela se teste.
 *
 * L'ÉTAT D'ÉDITION garde les nombres TELS QUE SAISIS (du texte) : « 1 334,31 » reste « 1 334,31 »
 * pendant la frappe, et n'est lu qu'à la validation. Une quantité vide veut dire « inconnue » —
 * jamais « 1 ». Un prix vide veut dire « pas de prix » — jamais « 0 € ».
 *
 * LE RANG EST LA POSITION. Le palier le plus bas de la liste est le moins complet, le dernier est le
 * plus complet : à un même point d'échéance, seul le plus complet s'applique. Le rang envoyé à l'API
 * est donc la position (1, 2, 3…), recalculée à chaque envoi.
 *
 * PRIX. Seul qui peut les voir ET les écrire envoie des prix. Les autres n'envoient aucune clé de
 * prix : le serveur reporte alors les prix des lignes conservées (par `lineKey`) au lieu de les
 * effacer. Garder la `lineKey` d'une ligne conservée est donc ce qui préserve son prix.
 *
 * Les règles de validation REFLÈTENT celles du serveur (`gamme-content.ts`,
 * `maintenance-schedule.ts`) pour éviter un aller-retour sur les erreurs évidentes ; le serveur
 * re-vérifie tout et reste l'autorité.
 */
import type {
  DesignationConnue,
  DimensionSeuil,
  GammeContenu,
  GenreLigneGamme,
  PrixConnu,
  ReferencesDuTarif,
} from '../api/client';
import { decrireSeuil } from './gammes';
import { nomDeSourceTarif } from './tarifs';

export interface SeuilEditable {
  readonly dimension: DimensionSeuil;
  readonly premier: string;
  readonly intervalle: string;
  /** Passage unique : pas d'intervalle. */
  readonly uneSeuleFois: boolean;
}

export interface LigneEditable {
  /** Identité stable de la ligne d'une version à l'autre (`lineKey`) : ne change jamais en éditant. */
  readonly cle: string;
  readonly genre: GenreLigneGamme;
  readonly designation: string;
  readonly reference: string;
  readonly quantite: string;
  readonly unite: string;
  readonly note: string;
  readonly equivalent: string;
  readonly fournisseurId: string | null;
  readonly prixPublic: string;
  readonly prixInterne: string;
}

export interface PalierEditable {
  /** Identité stable du palier d'une version à l'autre (`levelKey`). */
  readonly cle: string;
  readonly code: string;
  readonly libelle: string;
  /** Le temps de main d'œuvre prévu, en HEURES à la française (« 3 », « 1,5 »), par technicien ; vide = non renseigné (lot B23). */
  readonly tempsMo: string;
  readonly seuils: readonly SeuilEditable[];
  readonly lignes: readonly LigneEditable[];
}

export interface ContenuEditable {
  /** Du moins complet au plus complet : la position EST le rang. */
  readonly paliers: readonly PalierEditable[];
}

// ------------------------------------------------------------------ nombres

export type LectureNombre =
  | { readonly ok: true; readonly valeur: number | null }
  | { readonly ok: false; readonly erreur: string };

/**
 * Lit un nombre saisi à la française : « 1 334,31 » → 1334.31 (les espaces séparent les milliers,
 * la virgule ou le point séparent les décimales). Vide = `null`. Refuse tout ce qui n'est pas un
 * nombre, au lieu de le changer en 0 : un « 5O » (lettre O) ne doit pas devenir « 0 ».
 */
export function lireNombre(texte: string): LectureNombre {
  const brut = texte.replace(/\s/g, '');
  if (brut === '') return { ok: true, valeur: null };
  if (brut.includes('.') && brut.includes(',')) {
    return { ok: false, erreur: `« ${texte.trim()} » : écrivez 1334,31 (une seule virgule, pas de point entre les milliers).` };
  }
  const normalise = brut.replace(',', '.');
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(normalise)) {
    return { ok: false, erreur: `« ${texte.trim()} » n’est pas un nombre.` };
  }
  const valeur = Number(normalise);
  if (!Number.isFinite(valeur)) {
    return { ok: false, erreur: `« ${texte.trim()} » n’est pas un nombre.` };
  }
  return { ok: true, valeur };
}

/** Le texte à montrer dans un champ de saisie : virgule française, pas de séparateur de milliers. */
export function texteNombre(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n).replace('.', ',');
}

/** Le plafond d'un temps de main d'œuvre de palier : une journée par technicien. */
export const PLAFOND_TEMPS_MO_HEURES = 24;

/** Des minutes en heures à saisir : 90 → « 1,5 », 200 → « 3,33 », 180 → « 3 » ; `null` → vide. */
export function heuresDepuisMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '';
  return texteNombre(Math.round((minutes / 60) * 100) / 100);
}

/**
 * Le temps saisi en heures, converti en minutes entières : « 1,5 » → 90. Vide = `null` (non renseigné, jamais 0). Refuse un nombre
 * illisible, nul ou négatif, de plus de deux décimales, ou de plus d'une journée.
 */
export function minutesDepuisHeures(texte: string): { readonly ok: true; readonly minutes: number | null } | { readonly ok: false; readonly erreur: string } {
  const lu = lireNombre(texte);
  if (!lu.ok) return { ok: false, erreur: lu.erreur };
  if (lu.valeur === null) return { ok: true, minutes: null };
  if (lu.valeur <= 0) return { ok: false, erreur: 'le temps de main d’œuvre doit être supérieur à 0 (ou laissez la case vide).' };
  if (lu.valeur > PLAFOND_TEMPS_MO_HEURES) {
    return { ok: false, erreur: `le temps de main d’œuvre dépasse une journée (${PLAFOND_TEMPS_MO_HEURES} h par technicien).` };
  }
  if (!decimalesAuPlus(lu.valeur, 2)) return { ok: false, erreur: 'le temps de main d’œuvre : au plus 2 décimales (par exemple 1,5 ou 2,25).' };
  return { ok: true, minutes: Math.max(1, Math.round(lu.valeur * 60)) };
}

function decimalesAuPlus(n: number, chiffres: number): boolean {
  const facteur = 10 ** chiffres;
  return Math.abs(n * facteur - Math.round(n * facteur)) < 1e-6;
}

// ------------------------------------------------------------ constructeurs

/** Une clé neuve : une ligne ou un palier AJOUTÉ n'a pas d'ancêtre, il reçoit une identité à lui. */
export function nouvelleCle(): string {
  return globalThis.crypto.randomUUID();
}

export function seuilVide(dimension: DimensionSeuil = 'HOURS'): SeuilEditable {
  return { dimension, premier: '', intervalle: '', uneSeuleFois: false };
}

export function ligneVide(fournisseurId: string | null = null): LigneEditable {
  return {
    cle: nouvelleCle(),
    genre: 'REPLACE',
    designation: '',
    reference: '',
    quantite: '',
    unite: '',
    note: '',
    equivalent: '',
    fournisseurId,
    prixPublic: '',
    prixInterne: '',
  };
}

/** La prochaine lettre libre : A, B, C… puis A2, B2… quand l'alphabet est épuisé. */
export function prochainCode(codes: readonly string[]): string {
  const pris = new Set(codes.map((c) => c.trim().toUpperCase()));
  for (let tour = 0; tour < 100; tour += 1) {
    for (let i = 0; i < 26; i += 1) {
      const code = `${String.fromCharCode(65 + i)}${tour === 0 ? '' : tour + 1}`;
      if (!pris.has(code)) return code;
    }
  }
  return nouvelleCle().slice(0, 8).toUpperCase();
}

export function palierVide(codesExistants: readonly string[]): PalierEditable {
  return {
    cle: nouvelleCle(),
    code: prochainCode(codesExistants),
    libelle: '',
    tempsMo: '',
    seuils: [seuilVide()],
    lignes: [],
  };
}

// -------------------------------------------------------------- conversions

/** Le contenu rendu par l'API, mis en forme d'édition (paliers dans l'ordre de leur rang). */
export function versEditable(contenu: GammeContenu): ContenuEditable {
  const niveaux = [...contenu.levels].sort((a, b) => a.rank - b.rank);
  return {
    paliers: niveaux.map((n) => ({
      cle: n.levelKey,
      code: n.code,
      libelle: n.label,
      tempsMo: heuresDepuisMinutes(n.laborMinutes),
      seuils: n.thresholds.map((s) => ({
        dimension: s.dimension,
        premier: texteNombre(s.first),
        intervalle: texteNombre(s.interval),
        uneSeuleFois: s.interval === null,
      })),
      lignes: n.lines.map((l) => ({
        cle: l.lineKey,
        genre: l.kind,
        designation: l.designation,
        reference: l.reference ?? '',
        quantite: texteNombre(l.quantity),
        unite: l.unit ?? '',
        note: l.note ?? '',
        equivalent: l.adaptableReference ?? '',
        fournisseurId: l.adaptableSupplierPartyId,
        prixPublic: texteNombre(l.publicPrice),
        prixInterne: texteNombre(l.internalPrice),
      })),
    })),
  };
}

/** Vrai si le contenu d'édition diffère de celui d'origine : sert à la garde « non enregistré ». */
export function estModifie(origine: ContenuEditable, courant: ContenuEditable): boolean {
  return JSON.stringify(origine) !== JSON.stringify(courant);
}

// --------------------------------------------------------------- validation

export interface DefautContenu {
  /** Position du palier concerné (0 = premier), ou `null` si le défaut touche le contenu entier. */
  readonly palier: number | null;
  /** Position de la ligne concernée dans son palier, ou `null`. */
  readonly ligne: number | null;
  /** Message complet, en français, déjà préfixé « Palier B, ligne 3 : ». */
  readonly message: string;
}

function lieu(palier: PalierEditable, indexPalier: number, indexLigne: number | null): string {
  const nom = `Palier ${palier.code.trim() || `n° ${indexPalier + 1}`}`;
  return indexLigne === null ? nom : `${nom}, ligne ${indexLigne + 1}`;
}

const NOMS_DIMENSION: Record<DimensionSeuil, string> = { HOURS: 'heures', MONTHS: 'mois' };

/**
 * Tous les défauts évidents du contenu, dans l'ordre de lecture (paliers puis lignes). Liste vide :
 * le contenu est présentable à l'API. `options.avecPrix` : les colonnes de prix sont saisies.
 */
export function validerContenu(
  contenu: ContenuEditable,
  options: { readonly avecPrix: boolean },
): DefautContenu[] {
  const defauts: DefautContenu[] = [];
  const codes = new Map<string, number>();

  contenu.paliers.forEach((palier, ip) => {
    const ajouter = (ligne: number | null, message: string) =>
      defauts.push({ palier: ip, ligne, message: `${lieu(palier, ip, ligne)} : ${message}` });

    const code = palier.code.trim();
    if (code === '') ajouter(null, 'le code est obligatoire (A, B, C…).');
    else if (codes.has(code)) ajouter(null, `le code « ${code} » est déjà utilisé par un autre palier.`);
    else codes.set(code, ip);

    if (palier.libelle.trim() === '') ajouter(null, 'le libellé est obligatoire (par exemple « 6 000 h »).');

    const tempsMo = minutesDepuisHeures(palier.tempsMo);
    if (!tempsMo.ok) ajouter(null, tempsMo.erreur);

    if (palier.seuils.length === 0) {
      ajouter(null, 'il faut au moins un seuil (heures ou mois).');
    }
    const dimensions = new Set<DimensionSeuil>();
    for (const s of palier.seuils) {
      if (dimensions.has(s.dimension)) {
        ajouter(null, `deux seuils en ${NOMS_DIMENSION[s.dimension]} : un seul par type.`);
      }
      dimensions.add(s.dimension);

      const premier = lireNombre(s.premier);
      if (!premier.ok) ajouter(null, `premier seuil : ${premier.erreur}`);
      else if (premier.valeur === null || premier.valeur <= 0) {
        ajouter(null, `le premier seuil en ${NOMS_DIMENSION[s.dimension]} doit être supérieur à 0.`);
      } else if (!decimalesAuPlus(premier.valeur, 2)) {
        ajouter(null, 'premier seuil : au plus 2 décimales.');
      }

      if (!s.uneSeuleFois) {
        const intervalle = lireNombre(s.intervalle);
        if (!intervalle.ok) ajouter(null, `intervalle : ${intervalle.erreur}`);
        else if (intervalle.valeur === null || intervalle.valeur <= 0) {
          ajouter(
            null,
            `l’intervalle en ${NOMS_DIMENSION[s.dimension]} doit être supérieur à 0 — ou cochez « une seule fois ».`,
          );
        } else if (!decimalesAuPlus(intervalle.valeur, 2)) {
          ajouter(null, 'intervalle : au plus 2 décimales.');
        }
      }
    }

    palier.lignes.forEach((l, il) => {
      const faute = (message: string) => ajouter(il, message);

      if (l.designation.trim() === '') faute('la désignation est obligatoire.');

      const ref = l.reference.trim();
      const eq = l.equivalent.trim();
      if (l.genre === 'CHECK' && (ref !== '' || eq !== '')) {
        faute('un contrôle n’a pas de pièce : il ne porte aucune référence.');
      }
      if (eq !== '' && ref === '') faute('un équivalent suppose la référence officielle qu’il remplace.');
      if (eq !== '' && l.fournisseurId === null) faute(`le fournisseur de l’équivalent « ${eq} » est obligatoire.`);
      // Un fournisseur sans équivalent n'est PAS un défaut : une ligne neuve reprend celui de la
      // précédente pour épargner une saisie, et `versContenuEnvoye` ne l'envoie pas sans équivalent.

      const quantite = lireNombre(l.quantite);
      if (!quantite.ok) faute(`quantité : ${quantite.erreur}`);
      else if (quantite.valeur !== null) {
        if (quantite.valeur < 0) faute('la quantité ne peut pas être négative.');
        else if (!decimalesAuPlus(quantite.valeur, 3)) faute('quantité : au plus 3 décimales.');
      }

      if (options.avecPrix) {
        for (const [nom, texte] of [
          ['prix public', l.prixPublic],
          ['prix interne', l.prixInterne],
        ] as const) {
          const prix = lireNombre(texte);
          if (!prix.ok) faute(`${nom} : ${prix.erreur}`);
          else if (prix.valeur !== null) {
            if (prix.valeur < 0) faute(`le ${nom} ne peut pas être négatif.`);
            else if (!decimalesAuPlus(prix.valeur, 2)) faute(`${nom} : au plus 2 décimales (centimes).`);
          }
        }
      }
    });
  });

  return defauts;
}

// ------------------------------------------------------------------- envoi

export interface ContenuEnvoye {
  readonly levels: readonly {
    readonly levelKey: string;
    readonly code: string;
    readonly label: string;
    readonly rank: number;
    /** Minutes de main d'œuvre par technicien ; absent = non renseigné. */
    readonly laborMinutes?: number;
    readonly thresholds: readonly {
      readonly dimension: DimensionSeuil;
      readonly first: number;
      readonly interval: number | null;
    }[];
    readonly lines: readonly {
      readonly lineKey: string;
      readonly kind: GenreLigneGamme;
      readonly designation: string;
      readonly quantity: number | null;
      readonly unit: string | null;
      readonly note: string | null;
      readonly reference: string | null;
      readonly adaptableReference: string | null;
      readonly adaptableSupplierPartyId: string | null;
      readonly publicPrice?: number | null;
      readonly internalPrice?: number | null;
    }[];
  }[];
}

const vide = (texte: string): string | null => (texte.trim() === '' ? null : texte.trim());

/** La clé du temps de main d'œuvre, seulement quand il est renseigné et lisible (un contenu validé n'en a pas d'autre). */
function tempsMoEnvoye(texte: string): { readonly laborMinutes?: number } {
  const lu = minutesDepuisHeures(texte);
  return lu.ok && lu.minutes !== null ? { laborMinutes: lu.minutes } : {};
}

function nombreOuNull(texte: string): number | null {
  const lecture = lireNombre(texte);
  return lecture.ok ? lecture.valeur : null;
}

/**
 * Le contenu tel que l'API l'attend. À n'appeler que sur un contenu validé : un nombre illisible
 * deviendrait `null` ici.
 *
 * Sans `avecPrix`, AUCUNE clé de prix n'est envoyée (omises, pas mises à `null`) : le serveur
 * refuse un contenu qui en porte venant de qui n'a pas le droit de les écrire, et reporte alors
 * lui-même les prix des lignes conservées.
 */
export function versContenuEnvoye(contenu: ContenuEditable, options: { readonly avecPrix: boolean }): ContenuEnvoye {
  return {
    levels: contenu.paliers.map((p, i) => ({
      levelKey: p.cle,
      code: p.code.trim(),
      label: p.libelle.trim(),
      rank: i + 1,
      ...tempsMoEnvoye(p.tempsMo),
      thresholds: p.seuils.map((s) => ({
        dimension: s.dimension,
        first: nombreOuNull(s.premier) ?? 0,
        interval: s.uneSeuleFois ? null : nombreOuNull(s.intervalle),
      })),
      lines: p.lignes.map((l) => ({
        lineKey: l.cle,
        kind: l.genre,
        designation: l.designation.trim(),
        quantity: nombreOuNull(l.quantite),
        unit: vide(l.unite),
        note: vide(l.note),
        reference: vide(l.reference),
        adaptableReference: vide(l.equivalent),
        adaptableSupplierPartyId: vide(l.equivalent) === null ? null : l.fournisseurId,
        ...(options.avecPrix
          ? { publicPrice: nombreOuNull(l.prixPublic), internalPrice: nombreOuNull(l.prixInterne) }
          : {}),
      })),
    })),
  };
}

// --------------------------------------------------------- erreurs de l'API

const NOMS_CHAMPS: Record<string, string> = {
  levelKey: 'identifiant du palier',
  lineKey: 'identifiant de la ligne',
  code: 'code',
  label: 'libellé',
  rank: 'rang',
  laborMinutes: 'temps de main d’œuvre',
  designation: 'désignation',
  kind: 'genre',
  quantity: 'quantité',
  unit: 'unité',
  note: 'note',
  reference: 'référence',
  adaptableReference: 'équivalent',
  adaptableSupplierPartyId: 'fournisseur de l’équivalent',
  publicPrice: 'prix public',
  internalPrice: 'prix interne',
  dimension: 'type de seuil',
  first: 'premier seuil',
  interval: 'intervalle',
};

/**
 * Traduit un refus de l'API : « levels[0].lines[2].designation doit être un texte non vide. »
 * devient « Palier B, ligne 3 (désignation) : doit être un texte non vide. ». Un message qui ne
 * cite pas de chemin — il nomme déjà son palier, ou parle du contenu entier — est rendu tel quel.
 */
export function traduireErreurApi(message: string, contenu: ContenuEditable): string {
  const correspondance = /levels\[(\d+)\](?:\.lines\[(\d+)\]|\.thresholds\[(\d+)\])?(?:\.(\w+))?/.exec(message);
  if (!correspondance) return message;

  const [chemin, p, l, s, champ] = correspondance;
  const indexPalier = Number(p);
  const palier = contenu.paliers[indexPalier];
  if (!palier) return message;

  const place = lieu(palier, indexPalier, l === undefined ? null : Number(l));
  const precision = [
    s === undefined ? null : `seuil ${Number(s) + 1}`,
    champ === undefined ? null : (NOMS_CHAMPS[champ] ?? champ),
  ]
    .filter((x): x is string => x !== null)
    .join(', ');

  const reste = message
    .slice((correspondance.index ?? 0) + chemin.length)
    .replace(/^\s*:?\s*/, '')
    .trim();
  return `${place}${precision === '' ? '' : ` (${precision})`} : ${reste === '' ? 'valeur refusée.' : reste}`;
}

// ------------------------------------------------------- modifications pures

/** Déplace l'élément d'une place vers le haut (−1) ou le bas (+1) ; inchangé aux extrémités. */
export function deplacer<T>(liste: readonly T[], index: number, sens: -1 | 1): T[] {
  const cible = index + sens;
  const copie = [...liste];
  const element = copie[index];
  if (element === undefined || cible < 0 || cible >= copie.length) return copie;
  copie.splice(index, 1);
  copie.splice(cible, 0, element);
  return copie;
}

export function majPalier(
  contenu: ContenuEditable,
  index: number,
  fn: (palier: PalierEditable) => PalierEditable,
): ContenuEditable {
  return { paliers: contenu.paliers.map((p, i) => (i === index ? fn(p) : p)) };
}

export function majLigne(
  contenu: ContenuEditable,
  indexPalier: number,
  indexLigne: number,
  fn: (ligne: LigneEditable) => LigneEditable,
): ContenuEditable {
  return majPalier(contenu, indexPalier, (p) => ({
    ...p,
    lignes: p.lignes.map((l, i) => (i === indexLigne ? fn(l) : l)),
  }));
}

export function ajouterPalier(contenu: ContenuEditable): ContenuEditable {
  return { paliers: [...contenu.paliers, palierVide(contenu.paliers.map((p) => p.code))] };
}

export function retirerPalier(contenu: ContenuEditable, index: number): ContenuEditable {
  return { paliers: contenu.paliers.filter((_, i) => i !== index) };
}

export function deplacerPalier(contenu: ContenuEditable, index: number, sens: -1 | 1): ContenuEditable {
  return { paliers: deplacer(contenu.paliers, index, sens) };
}

/** Le fournisseur d'équivalent déjà utilisé le plus récemment : une ligne neuve le reprend. */
function dernierFournisseur(contenu: ContenuEditable, indexPalier: number): string | null {
  const ordre = [contenu.paliers[indexPalier], ...contenu.paliers.filter((_, i) => i !== indexPalier)];
  for (const palier of ordre) {
    for (const ligne of [...(palier?.lignes ?? [])].reverse()) {
      if (ligne.fournisseurId !== null) return ligne.fournisseurId;
    }
  }
  return null;
}

export function ajouterLigne(contenu: ContenuEditable, indexPalier: number): ContenuEditable {
  const fournisseur = dernierFournisseur(contenu, indexPalier);
  return majPalier(contenu, indexPalier, (p) => ({ ...p, lignes: [...p.lignes, ligneVide(fournisseur)] }));
}

export function retirerLigne(contenu: ContenuEditable, indexPalier: number, indexLigne: number): ContenuEditable {
  return majPalier(contenu, indexPalier, (p) => ({ ...p, lignes: p.lignes.filter((_, i) => i !== indexLigne) }));
}

export function deplacerLigne(
  contenu: ContenuEditable,
  indexPalier: number,
  indexLigne: number,
  sens: -1 | 1,
): ContenuEditable {
  return majPalier(contenu, indexPalier, (p) => ({ ...p, lignes: deplacer(p.lignes, indexLigne, sens) }));
}

/**
 * Duplique une ligne juste en dessous. La copie reçoit une NOUVELLE clé : deux lignes ne peuvent
 * pas porter la même identité, et la copie n'a pas d'ancêtre dont reporter le prix.
 */
export function dupliquerLigne(contenu: ContenuEditable, indexPalier: number, indexLigne: number): ContenuEditable {
  return majPalier(contenu, indexPalier, (p) => {
    const origine = p.lignes[indexLigne];
    if (!origine) return p;
    const lignes = [...p.lignes];
    lignes.splice(indexLigne + 1, 0, { ...origine, cle: nouvelleCle() });
    return { ...p, lignes };
  });
}

/** Ajoute le seuil du type qui manque (heures ou mois) : « 4 000 h ou 1 an ». */
export function ajouterSeuil(contenu: ContenuEditable, indexPalier: number): ContenuEditable {
  return majPalier(contenu, indexPalier, (p) => {
    const presents = new Set(p.seuils.map((s) => s.dimension));
    if (presents.size >= 2) return p;
    return { ...p, seuils: [...p.seuils, seuilVide(presents.has('HOURS') ? 'MONTHS' : 'HOURS')] };
  });
}

export function retirerSeuil(contenu: ContenuEditable, indexPalier: number, indexSeuil: number): ContenuEditable {
  return majPalier(contenu, indexPalier, (p) => ({ ...p, seuils: p.seuils.filter((_, i) => i !== indexSeuil) }));
}

/** Applique un équivalent connu à une ligne (référence et fournisseur d'un coup). */
export function utiliserEquivalent(
  contenu: ContenuEditable,
  indexPalier: number,
  indexLigne: number,
  equivalent: { readonly reference: string; readonly supplierPartyId: string },
): ContenuEditable {
  return majLigne(contenu, indexPalier, indexLigne, (l) => ({
    ...l,
    equivalent: equivalent.reference,
    fournisseurId: equivalent.supplierPartyId,
  }));
}

// ------------------------------------------------------- désignations connues

/** Combien d'alternatives on montre sous la désignation : le plus récent d'abord, pas un historique. */
export const DESIGNATIONS_PROPOSEES_MAX = 3;

/** Un texte sans tenir compte des espaces superflus ni des majuscules (comme le serveur les regroupe). */
function cleTexte(texte: string): string {
  return texte.replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr');
}

/**
 * « Tout part de la référence » : quand la référence d'une ligne vient d'être validée, sa désignation
 * connue REMPLIT la case, mais SEULEMENT si elle est vide. On ne touche jamais un texte saisi.
 *
 * Trois gardes, parce que la réponse arrive après coup :
 * - la ligne est retrouvée par sa clé, pas par sa position (elle a pu bouger) ;
 * - sa désignation doit être encore vide à l'arrivée de la réponse (l'utilisateur a pu taper entre-temps) ;
 * - sa référence doit être encore celle pour laquelle on a cherché (elle a pu changer).
 * Sans changement à faire, le même objet est rendu.
 */
export function remplirDesignationSiVide(
  contenu: ContenuEditable,
  cleLigne: string,
  referenceCherchee: string,
  designation: string,
): ContenuEditable {
  const texte = designation.trim();
  if (texte === '') return contenu;

  let change = false;
  const paliers = contenu.paliers.map((p) => {
    const lignes = p.lignes.map((l) => {
      if (l.cle !== cleLigne) return l;
      if (l.designation.trim() !== '') return l;
      if (l.reference.trim() !== referenceCherchee.trim()) return l;
      change = true;
      return { ...l, designation: texte };
    });
    return lignes.some((l, i) => l !== p.lignes[i]) ? { ...p, lignes } : p;
  });
  return change ? { paliers } : contenu;
}

/**
 * Les désignations à PROPOSER sous la case : toutes (les plus récentes d'abord) quand elle est vide ; les
 * AUTRES quand elle porte l'une des désignations connues (pour en changer d'un clic, par exemple après un
 * remplissage automatique) ; RIEN quand le texte a été écrit à la main — on ne propose pas de remplacer ce que
 * l'utilisateur a tapé.
 */
export function designationsAProposer(
  saisie: string,
  connues: readonly DesignationConnue[],
): readonly DesignationConnue[] {
  if (saisie.trim() === '') return connues.slice(0, DESIGNATIONS_PROPOSEES_MAX);
  const cle = cleTexte(saisie);
  if (!connues.some((c) => cleTexte(c.designation) === cle)) return [];
  return connues.filter((c) => cleTexte(c.designation) !== cle).slice(0, DESIGNATIONS_PROPOSEES_MAX);
}

// ------------------------------------------ référence retrouvée par son début

/** Moins de caractères utiles que cela : on ne cherche pas (le serveur applique la même règle). */
export const SUGGESTION_MIN_CARACTERES = 3;

/** Lettres et chiffres d'une saisie : la ponctuation et les espaces ne comptent pas (comme la clé du serveur). */
export function caracteresUtiles(saisie: string): number {
  return saisie
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]/g, '').length;
}

/** Une saisie assez longue pour qu'une recherche par début de référence ait un sens. */
export function peutSuggererReference(saisie: string): boolean {
  return caracteresUtiles(saisie) >= SUGGESTION_MIN_CARACTERES;
}

/**
 * Ce que le panneau de suggestions montre, ou `null` pour ne rien montrer :
 * - pas de réponse, ou le fabricant n'a aucun tarif en vigueur (`tarifs` vide) : rien à proposer ;
 * - la seule ligne rendue est celle qui est DÉJÀ tapée en entier : la liste n'apprendrait rien, et la
 *   désignation et le prix se proposent déjà d'eux-mêmes en quittant la case.
 * Un tarif qui existe mais où rien ne correspond se montre (« aucune référence ») : c'est une réponse.
 */
export function suggestionsAMontrer(saisie: string, rendu: ReferencesDuTarif | null): ReferencesDuTarif | null {
  if (!rendu || rendu.tarifs.length === 0) return null;
  const premiere = rendu.items[0];
  if (rendu.total === 1 && rendu.items.length === 1 && premiere && comparable(premiere.reference) === comparable(saisie)) {
    return null;
  }
  return rendu;
}

const comparable = (texte: string): string => texte.replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr');

/**
 * Le texte de la case peut-il être remplacé sans rien perdre de ce que l'utilisateur a écrit ? Oui s'il
 * est vide, ou s'il est l'un des textes que les AIDES ont posés ou proposés (désignations connues de la
 * référence précédente) : celui qu'on vient de voir arriver tout seul, pas une saisie à la main. Un texte
 * écrit à la main n'est jamais remplacé.
 */
export function designationRemplacable(saisie: string, textesDAide: readonly string[]): boolean {
  if (saisie.trim() === '') return true;
  const cle = cleTexte(saisie);
  return textesDAide.some((t) => cleTexte(t) === cle);
}

/**
 * Choisir une référence dans la liste : la référence ENTIÈRE est posée sur la ligne, et sa désignation
 * quand la case est vide ou qu'elle porte le texte d'une aide (`textesDAide`, voir `designationRemplacable`)
 * — typiquement le texte de la référence qu'on vient de quitter pour une autre. Un texte écrit à la main
 * n'est jamais remplacé. Rien d'autre ne change : le prix et l'équivalent se proposent ensuite par les
 * mêmes aides que lorsqu'on tape la référence.
 */
export function choisirReferenceTarif(
  contenu: ContenuEditable,
  indexPalier: number,
  indexLigne: number,
  choix: { readonly reference: string; readonly description: string },
  textesDAide: readonly string[] = [],
): ContenuEditable {
  return majLigne(contenu, indexPalier, indexLigne, (l) => ({
    ...l,
    reference: choix.reference,
    designation: designationRemplacable(l.designation, textesDAide) ? choix.description : l.designation,
  }));
}

// ------------------------------------------------------------- prix connus

export type ChampPrix = 'prixPublic' | 'prixInterne';

/** Combien de prix connus on montre sous une case de prix : le plus récent d'abord, pas un historique. */
export const PRIX_PROPOSES_MAX = 3;

/**
 * Au-delà de ce rapport (dans un sens ou dans l'autre) entre le prix saisi et les prix connus, l'écran
 * le signale. 2 : un prix qui double ou qui est divisé par deux n'est pas une mise à jour de tarif
 * ordinaire, c'est le plus souvent une erreur de saisie (45,29 € devenu 2 466,57 €).
 */
export const RAPPORT_PRIX_SUSPECT = 2;

/** Applique un prix connu à une ligne : le texte à la française, comme s'il avait été saisi. */
export function utiliserPrix(
  contenu: ContenuEditable,
  indexPalier: number,
  indexLigne: number,
  champ: ChampPrix,
  prix: number,
): ContenuEditable {
  const texte = texteNombre(prix);
  return majLigne(contenu, indexPalier, indexLigne, (l) =>
    champ === 'prixPublic' ? { ...l, prixPublic: texte } : { ...l, prixInterne: texte },
  );
}

/**
 * Les prix à PROPOSER sous une case : seulement quand elle est VIDE (on n'écrase jamais ce qui est
 * saisi), les plus récents d'abord. Une case remplie ne se voit proposer que le signalement d'un écart
 * (`prixEnEcart`).
 */
export function prixAProposer(saisi: string, connus: readonly PrixConnu[]): readonly PrixConnu[] {
  if (saisi.trim() !== '') return [];
  return connus.slice(0, PRIX_PROPOSES_MAX);
}

/**
 * Le prix connu à rappeler quand le prix SAISI s'en écarte beaucoup : le plus récent des prix
 * connus, ou `null` s'il n'y a rien à dire. Jamais un refus : une vraie hausse de tarif reste
 * possible, l'écran se contente de le faire remarquer.
 *
 * On ne signale que si le prix saisi est loin de TOUS les prix connus : un prix proche de l'un d'eux
 * (ou identique) est un prix déjà vu.
 */
export function prixEnEcart(saisi: string, connus: readonly PrixConnu[]): PrixConnu | null {
  const lu = lireNombre(saisi);
  if (!lu.ok || lu.valeur === null || lu.valeur <= 0) return null;
  const valeur = lu.valeur;
  const utilisables = connus.filter((c) => c.price > 0);
  const recent = utilisables[0];
  if (!recent) return null;

  const loinDeTous = utilisables.every((c) => {
    const rapport = valeur / c.price;
    return rapport >= RAPPORT_PRIX_SUSPECT || rapport <= 1 / RAPPORT_PRIX_SUSPECT;
  });
  return loinDeTous ? recent : null;
}

/**
 * D'où vient un prix ou une désignation connus, en mots : « KAESER DSDX302 · réf. DSDX.1 et 2 autres
 * gammes », ou, quand le TARIF du fabricant le porte : « Tarif KAESER pièces et service » (+ « et 2 gammes »
 * si des gammes le portent aussi). Tout ce qui sait nommer ses sources convient (un prix, une désignation).
 */
export function origineDuPrix(connu: {
  readonly nbGammes: number;
  readonly gammes: readonly { readonly label: string }[];
  readonly tarif?: { readonly label: string };
}): string {
  if (connu.tarif) {
    const source = nomDeSourceTarif(connu.tarif.label);
    if (connu.nbGammes <= 0) return source;
    return `${source} et ${connu.nbGammes} gamme${connu.nbGammes > 1 ? 's' : ''}`;
  }
  const premiere = connu.gammes[0]?.label ?? 'une gamme';
  const autres = connu.nbGammes - 1;
  if (autres <= 0) return premiere;
  return `${premiere} et ${autres} autre${autres > 1 ? 's' : ''} gamme${autres > 1 ? 's' : ''}`;
}

/**
 * L'infobulle d'un prix ou d'une désignation proposés : d'où il vient et depuis quand. Pour un tarif :
 * « Prix du tarif « KAESER pièces et service », en vigueur depuis le 01/01/2026 » ; pour des gammes :
 * « Prix de KAESER DSDX302 · réf. DSDX.1 et 2 autres gammes, en vigueur depuis le 07/10/2026 ».
 */
export function infobulleDeLaSource(
  nature: 'Prix' | 'Désignation',
  connu: {
    readonly validFrom: string;
    readonly nbGammes: number;
    readonly gammes: readonly { readonly label: string }[];
    readonly tarif?: { readonly label: string; readonly validFrom: string };
  },
): string {
  const [annee, mois, jour] = dateDeLaSource(connu).slice(0, 10).split('-');
  const depuis = `en vigueur depuis le ${jour}/${mois}/${annee}`;
  if (connu.tarif) {
    const gammes =
      connu.nbGammes > 0 ? `, aussi présent dans ${connu.nbGammes} gamme${connu.nbGammes > 1 ? 's' : ''}` : '';
    return `${nature} du tarif « ${connu.tarif.label.trim()} »${gammes}, ${depuis}`;
  }
  return `${nature} de ${origineDuPrix(connu)}, ${depuis}`;
}

/**
 * La date d'effet à annoncer pour un prix ou une désignation connus : celle du TARIF quand il le porte
 * (« en vigueur depuis le 01/01/2026 »), sinon celle de la gamme la plus récente.
 */
export function dateDeLaSource(connu: { readonly validFrom: string; readonly tarif?: { readonly validFrom: string } }): string {
  return connu.tarif ? connu.tarif.validFrom : connu.validFrom;
}

// ------------------------------------------------------------- présentation

/**
 * Le palier en mots, comme le lira l'écran de lecture : « tous les 6 000 h, le premier à 3 000 h »,
 * « une fois par an », « 4 000 h ou 1 an ». `null` tant qu'un seuil n'est pas lisible : on n'affiche
 * pas une phrase qui dirait autre chose que ce qui est saisi.
 */
export function decrirePalier(palier: PalierEditable): string | null {
  if (palier.seuils.length === 0) return null;
  const phrases: string[] = [];
  for (const s of palier.seuils) {
    const premier = lireNombre(s.premier);
    const intervalle = s.uneSeuleFois ? { ok: true as const, valeur: null } : lireNombre(s.intervalle);
    if (!premier.ok || premier.valeur === null || premier.valeur <= 0) return null;
    if (!intervalle.ok || (!s.uneSeuleFois && (intervalle.valeur === null || intervalle.valeur <= 0))) return null;
    phrases.push(decrireSeuil({ dimension: s.dimension, first: premier.valeur, interval: intervalle.valeur }));
  }
  return phrases.join(' ou ');
}

/** Combien de paliers et de lignes : pour annoncer ce qui vient d'être enregistré. */
export function compterContenu(contenu: ContenuEditable): { readonly paliers: number; readonly lignes: number } {
  return {
    paliers: contenu.paliers.length,
    lignes: contenu.paliers.reduce((n, p) => n + p.lignes.length, 0),
  };
}
